import { type FC } from 'react';
import { Button, FormHelperText } from '@selfinspectionorg/ui-kit';
import styles from './Mfa.module.scss';
import { Card } from '@/components/ui';

interface Props {
  mode: 'enroll' | 'verify';
  isRunning: boolean;
  error: string | null;
  /** Start (or retry) the ceremony. */
  onRun: () => void;
  onBack: () => void;
}

/**
 * The passkey start / retry surface. It hosts the explicit "create / use passkey" button — used as
 * the not-started screen (a sole-method landing or an enrollment step) and as the retry screen after
 * a cancelled or failed ceremony. The ceremony itself is owned by `usePasskeyCeremony`; this is pure
 * presentation. A verify ceremony fired straight from the method selector never renders this at all
 * unless it fails.
 */
export const PasskeyButton: FC<Props> = ({ mode, isRunning, error, onRun, onBack }) => {
  const isEnroll = mode === 'enroll';

  return (
    <>
      <Card.BackButton onClick={onBack} testId="mfa-back-button" />
      <Card.Title
        title={isEnroll ? 'Register a passkey' : 'Sign in with a passkey'}
        description={
          isEnroll
            ? 'Your browser will ask you to create a passkey using your device biometrics or a security key.'
            : 'Your browser will ask you to use your passkey to continue.'
        }
      />
      <div className={styles.body}>
        <Button width="100%" onClick={onRun} isLoading={isRunning} data-testid="mfa-passkey-button">
          {isEnroll ? 'Create passkey' : 'Use passkey'}
        </Button>
        {error && <FormHelperText errored>{error}</FormHelperText>}
      </div>
    </>
  );
};
